import { Component, Input } from "@angular/core";
import { animate, style, transition, trigger } from "@angular/animations";

/**
 * 4H brand loader — ciclo di 4 icone (heart/head/hands/results) con
 * dissolvenza 2.4s ease-in-out. Estratto da login-shell per riuso.
 *
 * Uso base (solo icone animate):
 *   <app-loader-4h></app-loader-4h>
 *
 * Con caption:
 *   <app-loader-4h label="Invio in corso..."></app-loader-4h>
 *
 * Con contatore animato "N / M":
 *   <app-loader-4h [count]="3" [total]="10" label="Invio"></app-loader-4h>
 *
 * Il wrapper applica `role="status"` + `aria-live="polite"` per i lettori
 * di schermo; il testo viene annunciato quando `count` cambia.
 *
 * Rispetta `prefers-reduced-motion: reduce` (animazioni disabilitate via SCSS).
 */
@Component({
  selector: "app-loader-4h",
  templateUrl: "./loader-4h.component.html",
  styleUrls: ["./loader-4h.component.scss"],
  animations: [
    trigger("counterPop", [
      transition(":increment, :decrement", [
        style({ transform: "scale(1.25)", color: "#C8273D" }),
        animate("320ms cubic-bezier(0.22, 1, 0.36, 1)", style({ transform: "scale(1)", color: "*" })),
      ]),
    ]),
    trigger("fadeInOut", [
      transition(":enter", [
        style({ opacity: 0, transform: "translateY(8px)" }),
        animate("300ms cubic-bezier(0.22, 1, 0.36, 1)", style({ opacity: 1, transform: "translateY(0)" })),
      ]),
      transition(":leave", [
        animate("200ms cubic-bezier(0.22, 1, 0.36, 1)", style({ opacity: 0, transform: "translateY(-6px)" })),
      ]),
    ]),
  ],
})
export class LoaderFourHComponent {
  /** Caption sotto alle icone (es. "Caricamento...", "Invio in corso..."). */
  @Input() label: string | null = null;

  /** Numero corrente (es. quantità già inviata). Se `null` il contatore è nascosto. */
  @Input() count: number | null = null;

  /** Totale (es. totale da inviare). Se null non mostrato. */
  @Input() total: number | null = null;

  /** Variante di dimensione: md (default, 56px) | sm (40px) | lg (72px). */
  @Input() size: "sm" | "md" | "lg" = "md";

  /** Altezza minima contenitore. Default 0 (flex naturale). */
  @Input() minHeight: string | null = null;

  /** Mostra la percentuale (0-100) in evidenza. Se true e sono disponibili
   *  `count`+`total`, viene calcolata. Altrimenti usa il valore esplicito
   *  `percentValue`. Utile per il modal "Invia questionari" dove la
   *  progressione è più leggibile in percentuale. */
  @Input() showPercent = false;

  /** Valore percentuale esplicito (0-100). Se null e showPercent=true,
   *  viene derivato da count/total. */
  @Input() percentValue: number | null = null;

  get hasCounter(): boolean {
    return this.count !== null && this.total !== null;
  }

  get percent(): number | null {
    if (!this.showPercent) return null;
    if (this.percentValue !== null && !isNaN(this.percentValue)) {
      return Math.max(0, Math.min(100, Math.round(this.percentValue)));
    }
    if (this.count !== null && this.total !== null && this.total > 0) {
      return Math.round((this.count / this.total) * 100);
    }
    return 0;
  }
}
